import { useState, useEffect, useCallback } from 'react';
import { useAuthentication } from '@ecosystem/newron-client-lib';
import { sendInsightSummary, transformScoreCard, fetchMyBrand } from './aiHelpers';
import { GenAiOptions, Params } from '../../types';
import { ScoreCardData } from './types';

export interface UseGenAIProps {
  chartData?: ScoreCardData[];
  config: GenAiOptions;
  params?: Params;
  trackGenAiClicked?: () => void;
}

export function useGenAI({ chartData, config, params, trackGenAiClicked }: UseGenAIProps) {
  const [insightSummary, setInsightSummary] = useState<any>(null);
  const [requestId, setRequestId] = useState<string>('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuthentication();

  useEffect(() => {
    setAiEnabled(!!config);
  }, [config]);

  const runGenAI = useCallback(async () => {
    if (!chartData || !config) return;
    setAiLoading(true);
    trackGenAiClicked?.();
    try {
      const myBrand = await fetchMyBrand(params || {});
      const grouping = params?.grouping || [];
      const result = await transformScoreCard(chartData, grouping, myBrand);
      const response = await sendInsightSummary(result, config.endpoint, auth.getAccessToken);
      setInsightSummary(response.smartInsights);
      setRequestId(response.requestId);
    } catch {
      setError('AI signals not available. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }, [chartData, auth, config, params]);

  const resetGenAI = useCallback(() => {
    setInsightSummary(null);
    setRequestId('');
    setError(null);
  }, []);

  return {
    insightSummary,
    requestId,
    aiEnabled,
    aiLoading,
    error,
    runGenAI,
    resetGenAI,
  };
}
